import {ApiClient} from '@hconnect/apiclient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import qs from 'qs'
import {useNavigate} from 'react-router'

import {TasksApiPath} from '../../consts'
import {mapTasksFiltersParams, mapTasksQueryParams} from '../../mappers'
import {PaginationResponse, HProduceTask, UseTasksParams} from '../../types'
import {handleTasksError as handleError} from '../../utils'

const TasksQueryKey = 'tasks'

type InfiniteTasksData = {
  pages: HProduceTask[][]
  totalItemCount: number
}

export const useTasksInfiniteQuery = (
  params: UseTasksParams,
  options?: Omit<
    UseInfiniteQueryOptions<PaginationResponse<HProduceTask>, AxiosError, InfiniteTasksData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam' | 'select'
  >
) => {
  const navigate = useNavigate()
  const {limit, sources, assignedToMe, createdByMe, filters, sorting, timezone, areaPlantIds} =
    params

  const result = useInfiniteQuery({
    queryKey: [
      TasksQueryKey,
      limit,
      sources,
      assignedToMe,
      createdByMe,
      filters,
      sorting,
      timezone,
      areaPlantIds
    ],
    queryFn: async ({pageParam = 0, signal}) => {
      const offset = pageParam as number
      const response = await ApiClient.axiosInstance.post<PaginationResponse<HProduceTask>>(
        TasksApiPath.TASKS,
        mapTasksFiltersParams(timezone, filters, areaPlantIds),
        {
          params: mapTasksQueryParams(params, offset),
          paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat'}),
          signal
        }
      )

      return response.data ?? []
    },
    getNextPageParam: (lastPage, pages) => {
      // in infinite query this will determine the offset for the next page as 'pageParam' prop in the queryFn,
      // and if it returns false, it means there are no more pages (hasNextPage property will be false)
      return lastPage.items.length === limit ? pages.length * limit : undefined
    },
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.items),
      totalItemCount: data.pages[0]?.totalItemCount ?? 0
    }),
    ...options
  })

  if (result.error) {
    handleError(result.error, navigate)
  }

  return result
}
